import React, { useCallback, useEffect, useState } from "react";
import { flushVisits, queuedVisits, retryVisit } from "../utils/offlineVisits";

const button = { border: 0, borderRadius: 7, padding: "6px 9px", fontSize: 11, fontWeight: 800, cursor: "pointer" };

function locationRows() {
  try {
    const user = JSON.parse(localStorage.getItem("crm_user") || "null");
    return JSON.parse(localStorage.getItem(`crm_location_queue_${user?.id || "self"}`) || "[]");
  } catch { return []; }
}

function signedInUser() {
  try { return JSON.parse(localStorage.getItem("crm_user") || "null"); }
  catch { return null; }
}

function visitDraft() {
  const user = signedInUser();
  if (!user?.id) return null;
  try {
    const draft = JSON.parse(localStorage.getItem(`ebek_crm_visit_draft_${user.id}`) || "null");
    if (!draft?.form) return null;
    const hasContent = [draft.form.institution_name, draft.form.contact_name, draft.form.comments,
      draft.form.contact_phone, draft.form.contact_email, draft.selectedLeadId]
      .some(value => String(value || "").trim());
    return hasContent ? draft : null;
  } catch { return null; }
}

export default function PwaStatus() {
  const [online, setOnline] = useState(navigator.onLine);
  const [rows, setRows] = useState(queuedVisits());
  const [gps, setGps] = useState(locationRows());
  const [issue, setIssue] = useState(localStorage.getItem("crm_location_tracking_issue") || "");
  const [prompt, setPrompt] = useState(null);
  const [open, setOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [draft, setDraft] = useState(visitDraft());

  const refresh = useCallback(() => {
    setRows(queuedVisits());
    setGps(locationRows());
    setIssue(localStorage.getItem("crm_location_tracking_issue") || "");
    setDraft(visitDraft());
  }, []);
  const sync = useCallback(async () => {
    if (!navigator.onLine) return;
    setSyncing(true);
    try {
      await flushVisits();
      window.dispatchEvent(new Event("crm-sync-request"));
      window.setTimeout(refresh, 300);
    } finally { setSyncing(false); }
  }, [refresh]);

  useEffect(() => {
    const up = () => { setOnline(true); sync(); };
    const down = () => setOnline(false);
    const install = (event) => { event.preventDefault(); setPrompt(event); };
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    window.addEventListener("offline-visits-changed", refresh);
    window.addEventListener("crm-location-queue-changed", refresh);
    window.addEventListener("crm-visit-draft-changed", refresh);
    window.addEventListener("beforeinstallprompt", install);
    const timer = setInterval(refresh, 15000);
    refresh();
    if (navigator.onLine) sync();
    return () => {
      clearInterval(timer);
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
      window.removeEventListener("offline-visits-changed", refresh);
      window.removeEventListener("crm-location-queue-changed", refresh);
      window.removeEventListener("crm-visit-draft-changed", refresh);
      window.removeEventListener("beforeinstallprompt", install);
    };
  }, [refresh, sync]);

  const user = signedInUser();
  if (!user?.id) return null;
  const pending = rows.length + gps.length;
  const failed = rows.filter(row => row.last_error).length;
  const old = rows.filter(row => Date.now() - new Date(row.queued_at).getTime() > 4 * 3600000).length;
  const attention = pending + (draft ? 1 : 0);

  return <>
    <div style={{ position:"fixed", left:8, bottom:8, zIndex:9998, display:"flex", gap:7, alignItems:"center", padding:"8px 10px", borderRadius:11, background:online ? (failed || issue ? "#fef2f2" : "#ecfdf5") : "#fff7ed", color:failed || issue ? "#b91c1c" : online ? "#047857" : "#c2410c", boxShadow:"0 3px 14px #0002", fontSize:11, fontWeight:800 }}>
      <button onClick={() => setOpen(true)} style={{ ...button, background:"transparent", color:"inherit", padding:0 }}>
        {attention ? `CRM Sync Centre · ${attention} item${attention === 1 ? "" : "s"}` : issue ? "CRM Sync Centre · GPS attention" : online ? "CRM Sync Centre · Synced" : "CRM Sync Centre · Offline"}
      </button>
      {online && pending > 0 && <button disabled={syncing} onClick={sync} style={{ ...button, background:"#0f766e", color:"#fff" }}>Retry now</button>}
      {prompt && <button onClick={async () => { await prompt.prompt(); setPrompt(null); }} style={{ ...button, background:"#0f766e", color:"#fff" }}>Install</button>}
    </div>
    {open && <div style={{ position:"fixed", inset:0, zIndex:10003, background:"#0f172a99", display:"grid", placeItems:"center", padding:15 }}>
      <div style={{ width:"min(560px,100%)", maxHeight:"84vh", overflow:"auto", background:"#fff", borderRadius:17, padding:18 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div><small style={{ fontWeight:900, color:"#0f766e", textTransform:"uppercase" }}>Offline reliability</small><h2 style={{ fontSize:18, margin:"4px 0" }}>CRM Sync Centre</h2></div>
          <button onClick={() => setOpen(false)} style={{ ...button, fontSize:20, background:"transparent" }}>×</button>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8, margin:"12px 0" }}>
          {[[pending,"Pending"],[failed,"Failed"],[old,"Over 4 hours"]].map(([value,label]) => <div key={label} style={{ padding:10, borderRadius:11, background:"#f8fafc", display:"grid" }}><b>{value}</b><small>{label}</small></div>)}
        </div>
        {issue && <div style={{ padding:12, background:"#fff7ed", borderRadius:11, color:"#9a3412", fontSize:12, fontWeight:700 }}>{issue}</div>}
        {!pending && !draft && !issue && <div style={{ padding:20, textAlign:"center", background:"#ecfdf5", borderRadius:12, color:"#047857", fontWeight:800 }}>✓ Everything is safely synced. You can close the app.</div>}
        {draft && <article style={{ border:"1px solid #f59e0b", background:"#fffbeb", borderRadius:11, padding:11, marginTop:8 }}>
          <b>{draft.form?.institution_name || "Unfinished institution visit"}</b>
          <div style={{ fontSize:10, color:"#92400e", marginTop:3 }}>Saved as a draft on this device. It has not reached the CRM server yet.</div>
          <button style={{ ...button, background:"#f59e0b", color:"#fff", marginTop:8 }} onClick={() => { setOpen(false); window.dispatchEvent(new Event("crm-open-visit-draft")); }}>Continue visit</button>
        </article>}
        {rows.map(row => <article key={row.client_sync_id} style={{ border:"1px solid #e2e8f0", borderRadius:11, padding:11, marginTop:8 }}>
          <b>{row.institution_name || "Institution visit"}</b>
          <div style={{ fontSize:10, color:"#64748b", marginTop:3 }}>Visit · queued {new Date(row.queued_at).toLocaleString("en-IN")} · retries {row.attempts || 0}</div>
          {row.last_error && <div style={{ fontSize:11, color:"#b91c1c", marginTop:5 }}>{row.last_error}</div>}
          {online && <div style={{ display:"flex", gap:7, marginTop:8 }}><button style={{ ...button, background:"#e0f2fe" }} onClick={async () => { await retryVisit(row.client_sync_id); refresh(); }}>Retry</button></div>}
        </article>)}
        {gps.length > 0 && <article style={{ border:"1px solid #e2e8f0", borderRadius:11, padding:11, marginTop:8 }}><b>GPS tracking points</b><div style={{ fontSize:10, color:"#64748b", marginTop:3 }}>{gps.length} location update{gps.length === 1 ? "" : "s"} waiting · retry automatically when connected</div></article>}
        <p style={{ fontSize:10, color:"#94a3b8" }}>Device: {navigator.userAgent.split(")")[0]}) · Do not clear browser data while items are pending.</p>
      </div>
    </div>}
  </>;
}
